import React from "react";
import { Link } from "react-router-dom";
import { MdPersonOutline, MdWarningAmber } from "react-icons/md";
import Card from "../../../../components/card";
import { MyWeek } from "../../../../interfaces/Dashboard";

/**
 * "Minha semana" — o bloco do colaborador.
 *
 * É o que sobra da home para quem não é gestor: a própria carga, as próprias tarefas e os dias sem
 * apontamento. Nada de receita da empresa.
 */

const WEEKDAYS = ["seg", "ter", "qua", "qui", "sex"];

const fmtHours = (h: number) => `${h.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h`;

const MyWeekCard: React.FC<{ data: MyWeek }> = ({ data }) => {
  const pct = Math.min(100, Math.round(data.utilizationPct));
  const tone =
    data.utilizationPct >= 100 ? "red" : data.utilizationPct >= 80 ? "amber" : "emerald";
  const bar = {
    red: "bg-gradient-to-r from-red-500 to-rose-400",
    amber: "bg-gradient-to-r from-amber-500 to-orange-400",
    emerald: "bg-gradient-to-r from-emerald-500 to-teal-400",
  }[tone];

  return (
    <Card extra="p-5">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MdPersonOutline className="h-5 w-5 text-dv-blue-500 dark:text-dv-cyan-300" aria-hidden="true" />
          <h3 className="text-base font-bold text-navy-700 dark:text-white">Minha semana</h3>
        </div>
        <Link
          to="/admin/timesheet"
          className="text-xs font-semibold text-dv-blue-600 hover:underline dark:text-dv-cyan-300"
        >
          Apontar horas
        </Link>
      </div>

      <div className="mb-4 mt-3">
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="text-gray-700 dark:text-gray-300">
            {fmtHours(data.plannedHours)} planejadas · {fmtHours(data.workedHours)} apontadas
          </span>
          <span className="font-semibold tabular-nums text-navy-700 dark:text-white">
            {fmtHours(data.capacityHours)} de capacidade
          </span>
        </div>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-900">
          <div className={`h-full rounded-full ${bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {data.daysWithoutEntry.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <MdWarningAmber className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            Sem apontamento em:{" "}
            <strong>{data.daysWithoutEntry.map((d) => WEEKDAYS[d - 1] ?? d).join(", ")}</strong>
          </span>
        </div>
      )}

      {data.tasks.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-gray-200 py-6 text-center text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300">
          Nenhuma tarefa planejada para esta semana.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {data.tasks.map((t) => (
            <Link
              key={t.taskId}
              to="/admin/project-tasks"
              className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 px-3 py-2 text-sm transition-colors hover:bg-gray-50 dark:border-white/10 dark:hover:bg-navy-700"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-navy-700 dark:text-white">{t.title}</p>
                <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                  {[t.clientName, t.projectName].filter(Boolean).join(" · ") || "Sem projeto"}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-semibold tabular-nums text-navy-700 dark:text-white">
                  {fmtHours(t.plannedHoursThisWeek)}
                </p>
                {t.isOverdue && (
                  <span className="text-[11px] font-semibold text-red-600 dark:text-red-400">Atrasada</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
};

export default MyWeekCard;
